'use client';

/**
 * @fileoverview The marketing-city surface's one answer to "is this visitor
 * signed in?" (WEB-640 / DES-60, shared by WEB-1171).
 *
 * Extracted verbatim from `CityChrome` so the city header and the page-body
 * conversion CTAs (the Tonight docket's "Save your picks · Create account")
 * read the SAME session state — no second auth source. Resolution order:
 *
 * 1. **SSR seed** — `initialAuthUser` from the shared server `/auth/me`
 *    probe. A signed-in visitor's first paint is already `authed`.
 * 2. **Probe** — `useGetCurrentUserQuery` (the RTK cache entry the
 *    `AuthProvider` shares), armed only when SSR saw a user / an auth cookie
 *    or the host-scoped session hint is set. Cold anon visitors never probe
 *    (that would 401 every public page).
 * 3. Only 401 / PARSING_ERROR settle to anon; a 5xx / network flap keeps the
 *    SSR seed (or stays pending) so a signed-in visitor is never downgraded.
 *
 * `authState === 'pending'` while an armed probe has not settled with no
 * identity yet: callers suppress conversion CTAs then, so a signed-in visitor
 * never flashes "Sign up" / "Create account".
 */

import * as React from 'react';
import { useGetCurrentUserQuery } from '@loci/api';

import { clearSessionHint, setSessionHint, useSessionHint } from '@/lib/auth/sessionHint';
import { cityAuthUserFromUnknown, type CityAuthUser } from '@/lib/city-nav/cityAuthUser';

export type CityAuthState = 'authed' | 'pending' | 'anon';

export interface UseCityAuthStateOptions {
  /** SSR-resolved identity (`resolveCityAuthUser`). */
  readonly initialAuthUser?: CityAuthUser | null;
  /** Server-observed `accessToken` / `refreshToken` cookie presence. */
  readonly hasAuthCookie?: boolean;
  /**
   * Write / clear the host-scoped session hint as the probe settles. Exactly
   * one mount per page owns this — `CityChrome`. Other readers leave it off.
   */
  readonly syncSessionHint?: boolean;
}

export interface CityAuthStateResult {
  readonly authUser: CityAuthUser | null;
  readonly isAuthPending: boolean;
  readonly authState: CityAuthState;
}

/**
 * RTK `/auth/me` errors that mean "definitely not signed in" (vs transient
 * network/5xx flaps). Mirrors `useAuthSession` so a 5xx does not wipe an
 * SSR-confirmed identity back to the anon "Sign up" chrome (WEB-640).
 */
export function isSettledUnauthenticatedProbeError(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('status' in error)) return false;
  if (error.status === 401) return true;
  if (error.status !== 'PARSING_ERROR') return false;
  // RTK reports a non-JSON body as PARSING_ERROR with the HTTP status in
  // `originalStatus`. A 5xx (e.g. a proxy's HTML 502) is a transient flap,
  // not a signed-out session: keep the SSR identity / pending state so the
  // guest CTA never appears and the session hint is not cleared.
  const originalStatus =
    'originalStatus' in error && typeof error.originalStatus === 'number'
      ? error.originalStatus
      : undefined;
  return originalStatus === undefined || originalStatus < 500;
}

export function useCityAuthState({
  initialAuthUser = null,
  hasAuthCookie = false,
  syncSessionHint = false,
}: UseCityAuthStateOptions = {}): CityAuthStateResult {
  const hasSessionHint = useSessionHint();
  // Probe when SSR saw a session/cookie OR the (host-scoped) hint is set.
  const shouldFetchUser = initialAuthUser !== null || hasAuthCookie || hasSessionHint;
  const { data, isSuccess, isError, error } = useGetCurrentUserQuery(undefined, {
    skip: !shouldFetchUser,
    // WEB-810: share the AuthProvider cache entry; do not re-probe on remount.
    refetchOnMountOrArgChange: false,
  });
  const probeSettledUnauthed = isError && isSettledUnauthenticatedProbeError(error);
  const liveAuthUser = React.useMemo(() => {
    if (!isSuccess) return undefined;
    const raw =
      data && typeof data === 'object' && 'user' in data
        ? (data as { user?: unknown }).user
        : undefined;
    return cityAuthUserFromUnknown(raw ?? null);
  }, [data, isSuccess]);

  // Success → trust the probe. Settled unauth (401/PARSING_ERROR) → anon.
  // Loading or transient error → keep the SSR seed (or stay pending if none).
  const authUser = !shouldFetchUser
    ? null
    : isSuccess
      ? (liveAuthUser ?? null)
      : probeSettledUnauthed
        ? null
        : initialAuthUser;

  const isAuthPending = shouldFetchUser && authUser === null && !isSuccess && !probeSettledUnauthed;

  React.useEffect(() => {
    if (!syncSessionHint) return;
    if (authUser) {
      setSessionHint();
    } else if (probeSettledUnauthed || (isSuccess && !authUser)) {
      clearSessionHint();
    }
  }, [syncSessionHint, authUser, probeSettledUnauthed, isSuccess]);

  return {
    authUser,
    isAuthPending,
    authState: authUser ? 'authed' : isAuthPending ? 'pending' : 'anon',
  };
}
